import type { ByteSource } from './bytes';
import { type ArchiveName, IGNORED_FILES, type IgnoredFile, type NamedSource } from './discs';
import { MpqArchive } from './mpq/archive';
import { gameVersionOf, peFileVersion } from './pe';

/**
 * Blizzard's digital installer, as owners download it from their Blizzard account: one folder per
 * game with `Installer.exe` and `Installer Tome.mpq`. The tome is an MPQ that keeps the game's
 * own archives whole, stored without compression or encryption:
 *
 *  - Diablo II: `Common\d2data.mpq`, `d2char.mpq`, `d2sfx.mpq`, `d2speech.mpq`, `d2music.mpq`,
 *    `d2video.mpq`, its own `patch_d2.mpq`, `PC\Game.exe`…
 *  - Lord of Destruction: `Common\d2exp.mpq`, `d2xmusic.mpq`, `d2xtalk.mpq`, `d2xvideo.mpq`,
 *    `patch_d2.mpq`, `PC\Game.exe`…
 *
 * The inner archives are mounted where they lie (`MpqArchive.fileSource`): nothing is extracted.
 * Installing LoD replaces the classic installer's `patch_d2.mpq` and `Game.exe` with its own, so
 * only the LoD tome's are used.
 */
const TOME_NAME = /installer[ _-]?tome[^\\/]*\.mpq$/i;

/** True for `Installer Tome.mpq` (any case; also renamed copies such as `D2ES Installer Tome.mpq`). */
export function isInstallerTome(fileName: string): boolean {
  return TOME_NAME.test(fileName.split(/[\\/]/).pop()!);
}

/** The game archives an installer tome may hold, under `Common\`. */
const TOME_ARCHIVES: readonly ArchiveName[] = ['d2data.mpq', 'd2char.mpq', 'd2sfx.mpq', 'd2speech.mpq', 'd2music.mpq', 'd2exp.mpq', 'd2xmusic.mpq', 'd2xtalk.mpq'];

/** Archives in a tome that are recognised and never used. */
const TOME_UNUSED = ['d2video.mpq', 'd2xvideo.mpq'];

export type TomeGame = 'd2' | 'lod';

export interface TomeContents {
  /** Which game's installer: classic Diablo II, Lord of Destruction, or neither. */
  game: TomeGame | null;
  /** The installer's game version, from its `Game.exe` ("1.14b"), null if it has none. */
  version: string | null;
  /** "Lord of Destruction installer (1.14b)" */
  title: string;
  sources: NamedSource[];
}

/**
 * Opens an installer tome and finds the game archives in it (plus, in the LoD tome, the
 * installed patch archive and `Game.exe`, which names its version). Recognised archives that are
 * not used (cinematics) are appended to `ignored` when given. Throws if `src` is not an MPQ.
 */
export async function archivesInTome(src: ByteSource, label: string, ignored?: IgnoredFile[]): Promise<TomeContents> {
  const tome = await MpqArchive.open(src, label);
  const game: TomeGame | null = tome.has('Common\\d2exp.mpq') ? 'lod' : tome.has('Common\\d2data.mpq') ? 'd2' : null;
  const sources: NamedSource[] = [];
  const add = (inner: string, name: ArchiveName) => {
    const source = tome.fileSource(inner);
    if (source) sources.push({ name, source, origin: `${label} → ${inner}` });
  };
  for (const n of TOME_ARCHIVES) add(`Common\\${n}`, n);
  for (const n of TOME_UNUSED) if (tome.has(`Common\\${n}`)) ignored?.push({ name: n, origin: `${label} → Common\\${n}`, reason: IGNORED_FILES[n] });
  const exe = tome.fileSource('PC\\Game.exe');
  const v = exe ? await peFileVersion(exe).catch(() => null) : null;
  const version = v ? gameVersionOf(v) : null;
  if (game === 'lod') {
    add('Common\\patch_d2.mpq', 'patch_d2.mpq');
    if (exe) sources.push({ name: 'game.exe', source: exe, origin: `${label} → PC\\Game.exe` });
  }
  const what = game === 'lod' ? 'Lord of Destruction installer' : game === 'd2' ? 'Diablo II installer' : 'Installer without Diablo II archives';
  return { game, version, title: version ? `${what} (${version})` : what, sources };
}
